/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { getValue } from 'remote-config-value'
import { makeStyles } from '@material-ui/core/styles'
import { required, link, noSpaces, minlength } from 'validation-rules'
import { Select, TextField, Typography, Infotip, RequiredAsterisk, Checkbox } from 'foundations-library/components'
import MessageFlow from './MessageFlow'
import StepTitle from '../../shared/StepTitle'
import useFlatForm from '../../../hooks/useFlatForm'
import PrivacyPolicyLink from '../PrivacyPolicyLink'
import { replacePlaceholderWithActualBrandValues, useCasesOptions } from '../../../utils/util'
import UseCasesCheckboxes from './UseCasesCheckboxes'
import BrandsContext from '../../../contexts/BrandsContext'
import useDisableInput from '../../../hooks/useDisableInput'

const useStyles = makeStyles((theme) => {
    return {
        container: {
        },
        form: {
            display: 'flex',
            flexDirection: 'column',
            gap: 30
        },
        infotip: {
            maxWidth: 253
        }
    }
})

const getConsentIntoInitialValue = (wizardData, editing) => {
    if (wizardData?.consentInfo) return wizardData.consentInfo
    if (!editing) return {}
    const cInfo = editing.consent_info || editing.consentInfo
    if (typeof cInfo === 'string') {
        return JSON.parse(cInfo)
    }

    return cInfo
}

/**
 *
 */
export const getUsecases = (wizardData, editing) => {
    if (wizardData?.useCases) return wizardData.useCases
    if (!editing) return []

    let useCases = useCasesOptions.filter((x) => x.backendProp && editing[x.backendProp]).map((x) => x.id)
    if (editing.uc_other) useCases.push(editing.uc_other)
    useCases = useCases.filter(Boolean)
    if (!useCases.length && editing.use_cases.length) {
        useCases = editing.use_cases
    }
    return [...(new Set(useCases.filter(Boolean)))]
}

/**
 * @param {object} props
 */
const Usage = ({
    editing,
    ...props
}) => {
    const campaignUsageSavedData = props?.passData?.campaign_usage

    const { verifiedBrands } = useContext(BrandsContext)
    const {
        data,
        onClear,
        setDataProp,
        setIsValidProp,
        isFormDataValid,
        setMultipleIsValidProps,
        setMultipleDataProps
    } = useFlatForm({
        selectedBrand: campaignUsageSavedData?.selectedBrand || (verifiedBrands.length === 1 ? verifiedBrands[0] : (editing != null ? verifiedBrands.find(x => x.csp_brand_id === editing.csp_brand_id) : null)),
        campaignName: campaignUsageSavedData?.campaignName || (editing != null ? editing.name : ''),
        useCases: getUsecases(campaignUsageSavedData, editing),
        privacyPolicyLink: campaignUsageSavedData?.privacyPolicyLink || (editing != null ? editing.privacy_policy_url : ''),
        isPrivacyPolicyAccepted:
            (campaignUsageSavedData && Object.hasOwnProperty.call(campaignUsageSavedData, 'isPrivacyPolicyAccepted'))
                ? campaignUsageSavedData.isPrivacyPolicyAccepted
                : (editing != null ? editing.isPrivacyPolicyAccepted : false),
        messageFlow: campaignUsageSavedData?.messageFlow || (editing != null ? editing.messageflow : ''),
        consentInfo: getConsentIntoInitialValue(campaignUsageSavedData, editing),
        files: campaignUsageSavedData?.files || (editing != null ? editing.documents : [])
    })
    const {
        disableAllInputs,
        disableOnEditing
    } = useDisableInput({ mode: props.mode, item: editing })

    const classes = useStyles()

    useEffect(() => {
        if (props.saveId) props.update?.({ completed: true, gotoNext: true, saveData: data })
    }, [props.saveId])

    useEffect(() => {
        props.update?.({ disableNextButton: () => !isFormDataValid })
    }, [isFormDataValid])

    const onBrandChange = (v) => {
        setDataProp('selectedBrand')(verifiedBrands.find((x) => x.csp_brand_id === v))
    }

    const infotipTextValue = (field = 'it') => {
        if (disableOnEditing && props.mode === 'editing') {
            return `You cannot edit this field, to change ${field} you will have to start a new campaign application.`
        }

        return ''
    }

    return (
        <div className={classes.container} data-testid='usage'>
            <StepTitle
                remoteConfigID='sms_enrollment_campaigns_1st_step_title'
                status={editing?.status}
                mode={props.mode}
                onClickEdit={props.onClickEdit}
                onClickSubmitPaymentFailed={props.onClickSubmitPaymentFailed}
            />
            <div className={classes.form}>
                <Infotip value={infotipTextValue('brand')} bottom className={classes.infotip} data-testid='usage-brand-infotip'>
                    <Select
                        id='brandAutocomplete'
                        onChange={onBrandChange}
                        value={data.selectedBrand?.csp_brand_id}
                        options={verifiedBrands
                            .map((b) => ({ text: b.name, value: b.csp_brand_id }))
                        }
                        disabled={disableAllInputs || disableOnEditing}
                        isValid={setIsValidProp('selectedBrand')}
                        onClear={onClear('selectedBrand')}
                        rules={[required()]}
                        label='Brand'
                        data-testid='usage-select'
                    />
                </Infotip>
                <Infotip value={infotipTextValue('campaign name')} bottom className={classes.infotip} data-testid='usage-campaign-infotip'>
                    <TextField
                        id='campaignNameTxtArea'
                        value={data.campaignName}
                        label={getValue('sms_enrollment_campaign_name_placeholder')}
                        onChange={setDataProp('campaignName')}
                        onClear={onClear('campaignName')}
                        isValid={setIsValidProp('campaignName')}
                        helperText={'Minimum 3 characters'}
                        rules={[
                            required(),
                            minlength(3)
                        ]}
                        disabled={disableAllInputs || disableOnEditing}
                        data-testid='usage-name'
                    />
                </Infotip>
                <div>
                    <Typography
                        remoteConfigID='sms_enrollment_campaigns_1st_step_checkboxes_label'
                        tag='span'
                        data-testid='usage-cb-label'
                    />
                    <RequiredAsterisk />
                    <Infotip value={infotipTextValue('this section')} bottom className={classes.infotip} data-testid='usage-usecases-infotip'>
                        <UseCasesCheckboxes
                            value={data.useCases}
                            onChange={setDataProp('useCases')}
                            disabled={disableAllInputs || disableOnEditing}
                            isValid={setIsValidProp('useCases')}
                        />
                    </Infotip>
                </div>
                <MessageFlow
                    initialValue={{
                        messageFlow: data.messageFlow,
                        consentInfo: data.consentInfo,
                        files: data.files
                    }}
                    onChange={setMultipleDataProps}
                    isValid={setMultipleIsValidProps}
                    disabled={disableAllInputs}
                    item={editing}
                    useCases={data.useCases}
                    brand={data.selectedBrand}
                />
                <PrivacyPolicyLink
                    type='url'
                    value={data.privacyPolicyLink}
                    onChange={setDataProp('privacyPolicyLink')}
                    label={getValue('sms_enrollment_campaigns_1st_link_to_privacy_policy')}
                    helperText={getValue('sms_enrollment_campaigns_1st_link_to_privacy_policy_help_text')}
                    isValid={setIsValidProp('privacyPolicyLink')}
                    rules={[required(), noSpaces(), link()]}
                    onClear={onClear('privacyPolicyLink')}
                    disabled={disableAllInputs}
                    sample={replacePlaceholderWithActualBrandValues(
                        getValue('sms_enrollment_campaigns_1st_sample_privacy_policy'),
                        data.selectedBrand,
                        data.useCases
                    )}
                    data-testid='usage-pp-link'
                >
                    <Checkbox
                        checked={data.isPrivacyPolicyAccepted}
                        onChange={setDataProp('isPrivacyPolicyAccepted')}
                        isValid={setIsValidProp('isPrivacyPolicyAccepted')}
                        rules={[required()]}
                        disabled={disableAllInputs}
                        data-testid='usage-is-pp-accepted'
                    >I acknowledge the privacy policy includes all requirements</Checkbox>

                </PrivacyPolicyLink>
            </div>
        </div>
    )
}

Usage.propTypes = {
    passData: PropTypes.object,
    update: PropTypes.func,
    saveId: PropTypes.number,
    editing: PropTypes.object,
    preview: PropTypes.object,
    showContainer: PropTypes.bool,
    showTitle: PropTypes.bool,
    mode: PropTypes.string,
    onClickEdit: PropTypes.func,
    onClickSubmitPaymentFailed: PropTypes.func
}

export default Usage
